// 剣舞の旋回突きを、アルレッキーノ (Lies of P) の4連に落とし込んだ版 (2026-10-25・開発版 ?bladePirouette=b4)。
//   作者「剣舞の技の数は全然増やしていいよ。ひとまずアルレッキーノを剣舞に落とし込む形で作ってもらって、実プレイしながら調整していきたい。」
//   技の組み立て = docs/research/2026-10-25-arlecchino-attack-analysis.md (旋回突き β)。
//   流れ: 構えの見得 → ① 斬り上げ → ② 前へ水平 → 回る → ③ 大薙ぎ → 低く溜める (長い間) → ④ 低い突き → 残心 → 立ち姿。
//   拍: ①→②→③ は 0.45秒おき、③→④ だけ溜めの 0.55秒を足して 1.0秒 (ずらしの一拍・アルレッキーノの4発目の遅れ)。
//   4発とも弾ける (配布版の旋回突きは赤い1発の突き・弾けない)。締めの後は肩で息をする残心で、戻りが一番長い = 殴り返す隙。
//   絵 = public/sprites/bosses/blade-dancer/m1025_p4_{1..8}.png (倍率と横の基準は sprites.ts の剣舞の表・倍率 2.1)。
//   当たりの前端 = 当たる絵の剣先 (画面 px・足元から前): ① 130・② 169・③ 135・④ 163。
import { ATTACKS } from './attacks'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { SMEAR_ARTS, type ContactSmearArc } from './render'
import type { AttackDef } from './types'
import { BLADE_SMEAR_BASE, bladeDancerV6Smear, TENSE, withDissolves, type BladeTrailStyle } from './blade_dancer_art'

const BOSS_ID = 'blade-dancer'
const P = (n: number) => `m1025_p4_${n}`
const SETTLE = 'm1020_waltz_s2'   // 立ち姿の手前 (円舞の溜めの入りと同じ)
const STAND = 'm1020_waltz_c6'    // 立ち姿へ膝を伸ばしていく途中

// 拍 (ms)。当たりから次の当たりへ移る所でゲームは1コマ遅れるので、間からそのぶんを先に引く (円舞の Y2tz と同じ)。
const TICK = 16
const ACTIVE_MS = 130
const BEAT_MS = 450
const CROUCH_MS = 550
export const BLADE_PIROUETTE_B4_GAPS_MS = [BEAT_MS - ACTIVE_MS - TICK, BEAT_MS - ACTIVE_MS - TICK, BEAT_MS + CROUCH_MS - ACTIVE_MS - TICK]
const CHARGE_MS = 700
const RECOVERY_MS = 1000

export const BLADE_PIROUETTE_B4_FRONT_PX = [130, 169, 135, 163] as const
const REACH_FIT_MARGIN_PX = 15
const REAR_PX = -100
// 寄り方は配布版の円舞 (v2・v6) と同じ: 溜めの最後で滑らかに踏み出し、連撃の間だけ滑って寄る (当たる絵の間は止まる)。
const STEP_IN_MS = 180
const STOP_BUFFER_PX = 8
const LUNGE_PX = 200

export const BLADE_PIROUETTE_B4_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SETTLE, hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.15, image: P(1), hold: true, ease: 'easeIn' },               // 剣を低く引いて半身に構える (見得)
  { phase: 'telegraph', at: 0.86, image: P(1), hold: true, ...TENSE },
  { phase: 'active', at: 0, image: P(2), hold: true, tag: 'contact', hitIndex: 0 },        // ① 前下から斬り上げる
  { phase: 'comboGap', at: 0, image: P(2), hold: true, tag: 'followThrough', hitIndex: 0 },
  { phase: 'active', at: 0, image: P(3), hold: true, tag: 'contact', hitIndex: 1 },        // ② 返して胸の高さを前へ払う
  { phase: 'comboGap', at: 0, image: P(3), hold: true, tag: 'followThrough', hitIndex: 1 },
  { phase: 'comboGap', at: 0.45, image: P(4), hold: true, hitIndex: 1 },                    // 背を見せて回る
  { phase: 'active', at: 0, image: P(5), hold: true, tag: 'contact', hitIndex: 2 },        // ③ 回った勢いで大きく薙ぐ
  { phase: 'comboGap', at: 0, image: P(5), hold: true, tag: 'followThrough', hitIndex: 2 },
  { phase: 'comboGap', at: 0.3, image: P(6), hold: true, hitIndex: 2, ease: 'easeIn' },     // 低く沈んで剣を引き絞る (ずらしの溜め)
  { phase: 'comboGap', at: 0.86, image: P(6), hold: true, hitIndex: 2, ...TENSE },
  { phase: 'active', at: 0, image: P(7), hold: true, tag: 'contact', hitIndex: 3 },        // ④ 低く一直線に突く
  { phase: 'recovery', at: 0, image: P(7), hold: true, tag: 'followThrough' },
  { phase: 'recovery', at: 0.22, image: P(8), hold: true, tag: 'recover', ease: 'easeOut', soft: true },   // 残心・肩で息をする
  { phase: 'recovery', at: 0.72, image: STAND, hold: true, soft: true },
  { phase: 'recovery', at: 0.86, image: 'idle', hold: true },
], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })

// 剣先の通り道。どの弧も終わりが当たる絵の剣先 (前, 上) に乗る (剣先 = anchor + 半径 × (cos 終わり, sin 終わり))。
//   ① (130, 175)・② (169, 100)・③ (135, 94)・④ (163, 50)。
export const BLADE_PIROUETTE_B4_SMEAR: Record<string, ContactSmearArc> = {
  // ① 前の足元から頭の高さまで斬り上げる。
  [P(2)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 20, anchorUpPx: 100, radiusPx: 133.1, angleFromRad: -0.8, angleToRad: 0.598 },
  // ② 体の後ろから胸の高さを前へ平らに払う (大きな半径の弧の底)。
  [P(3)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 0, anchorUpPx: 330, radiusPx: 285.4, angleFromRad: -1.9, angleToRad: -0.937, trailHeadStart: 0.35 },
  // ③ 回った勢いで、後ろの低い所から前へ大きく薙ぐ。
  [P(5)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: -20, anchorUpPx: 300, radiusPx: 257.8, angleFromRad: -2.2, angleToRad: -0.925, widthPx: 8, trailHeadStart: 0.35 },
  // ④ 腰より低い一直線の突き (ほぼ直線になる大きな半径の弧で、剣の通る線だけを引く)。
  [P(7)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 60, anchorUpPx: 1050, radiusPx: 1005.3, angleFromRad: -1.568, angleToRad: -1.468, widthPx: 7 },
}
const THRUST_TRAIL_WIDTH_PX = 18

// アルレ版の5連 (10-27 作者「1回に見えるが、戻る際にも判定が欲しい。突くので1回、突いた後切り開くように自分の身の方へ両手の剣を引き戻すので1回の計2撃」)。
//   答えるページ blade-pirouette-pull-1027 の答え: 突き = ④ 低い突き (p4_7)・引き戻し = 残心の絵 (p4_8)・通り道 = B「交差させて手前へ」。
//   ③ 大きく薙ぐ (p4_5) → 低く溜める (p4_6c・ずらしの一拍 1.0秒) → ④ 低く突く → 300ms (3拍子の2刻み) → ⑤ 2本を交差させながら突いた高さのまま引き戻す (p4_8)。
//   ⑤ の当たりの前端 = 通り道の始まり = ④ の剣先 163px (引き戻しは突いた所から切り始めるので、④ が届く所にいれば当たる)。
//   p4_8 の剣先 (前, 上) = 前の剣 (87, 34)・後ろの剣 (-80, 25) (絵の画素 x 4 と 636 を、④ の剣先 163px を物差しにして倍率の比 2.566/2.625 で直した)。
const PULL_MS = 300
export const BLADE_PIROUETTE_ARLE_GAPS_MS = [BEAT_MS - ACTIVE_MS - TICK, BEAT_MS - ACTIVE_MS - TICK, BEAT_MS + CROUCH_MS - ACTIVE_MS - TICK, PULL_MS - ACTIVE_MS - TICK]
export const BLADE_PIROUETTE_ARLE_FRONT_PX = [130, 169, 135, 163, 163] as const
export const PIROUETTE_PULL = P(8)
export const PIROUETTE_COIL = 'm1026_p4_6c'
export const BLADE_PIROUETTE_ARLE_TIMELINE: BossAttackTimeline = withDissolves([
  ...BLADE_PIROUETTE_B4_TIMELINE.filter(k => k.phase === 'telegraph' || ((k.phase === 'active' || k.phase === 'comboGap') && (k.hitIndex ?? 0) <= 1)),
  { phase: 'active', at: 0, image: P(5), hold: true, tag: 'contact', hitIndex: 2 },        // ③ 回った勢いで大きく薙ぐ
  { phase: 'comboGap', at: 0, image: P(5), hold: true, tag: 'followThrough', hitIndex: 2 },
  { phase: 'comboGap', at: 0.3, image: PIROUETTE_COIL, hold: true, hitIndex: 2, ease: 'easeIn' },   // 低く沈んで剣を引き寄せる (ずらしの溜め)
  { phase: 'comboGap', at: 0.86, image: PIROUETTE_COIL, hold: true, hitIndex: 2, ...TENSE },
  { phase: 'active', at: 0, image: P(7), hold: true, tag: 'contact', hitIndex: 3 },        // ④ 低く一直線に突く
  { phase: 'comboGap', at: 0, image: P(7), hold: true, tag: 'followThrough', hitIndex: 3 },
  { phase: 'active', at: 0, image: PIROUETTE_PULL, hold: true, tag: 'contact', hitIndex: 4 }, // ⑤ 2本を交差させて手前へ引き戻す
  { phase: 'recovery', at: 0, image: PIROUETTE_PULL, hold: true, tag: 'followThrough' },
  { phase: 'recovery', at: 0.3, image: PIROUETTE_PULL, hold: true, tag: 'recover', ease: 'easeOut', soft: true },   // 残心・肩で息をする
  { phase: 'recovery', at: 0.54, image: 'm1020_v6_g2', hold: true, ease: 'easeOut', soft: true },   // 10-28 低い構えから半ば立ち上がり、剣を下ろして立つ (両腕を広げる締めへ一足に跳ねない)
  { phase: 'recovery', at: 0.63, image: SETTLE, hold: true, ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.72, image: STAND, hold: true, soft: true },
  { phase: 'recovery', at: 0.86, image: 'idle', hold: true },
], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })
// 引き戻しの弧2本 (通り道 B)。どちらも ④ の剣先 (163, 50) の上下から始まり、体の前で交差する。
//   前の剣: 少し下へたわみながら (127, 38) を通り、p4_8 の前の剣先 (87, 34) へ。
//   後ろの剣: 少し上へふくらみ、腰の前 (39, 66) を通って体の後ろの剣先 (-80, 25) へ。
export const BLADE_PIROUETTE_ARLE_SMEAR: Record<string, ContactSmearArc> = {
  ...BLADE_PIROUETTE_B4_SMEAR,
  [PIROUETTE_PULL]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 97.8, anchorUpPx: 138.8, radiusPx: 105.4, angleFromRad: -0.904, angleToRad: -1.674, widthPx: 7 },
  [`${PIROUETTE_PULL}#2`]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 58.8, anchorUpPx: -187.2, radiusPx: 253.6, angleFromRad: 1.148, angleToRad: 2.15, widthPx: 7 },
}

// 引き戻しの跡の作り直し (10-27 作者「交差が分からない…エフェクトの表現方法を変えるべき。どうすればいいか考えて作って」・開発版 ?bladePullFx=a|b|c)。
//   今の跡 (上) が読めない理由 = 2本が交わるのが出だしのすぐ (前 145px あたり) で、そこは帯の一番細く暗い所・交わる角度も浅い・
//   2本目は半径が大きく帯が太いので、1本目を塗りつぶして1枚の面に溶ける。
//   作り直しの通り道: 2本とも突いた高さの前 (122, 57) で交わる。前の剣は上 (188, 100) から斜めに下ろして p4_8 の前の剣先 (84, 30) へ、
//   後ろの剣は下 (188, 8) から斜めに上げ、交点を抜けて腰の前を回り体の後ろの剣先 (-80, 26) へ。交わる角度は約70度。
//   a 二閃 = 細い帯2本を 0.08 秒ずらして引く (X を一画ずつ書く)・2本目が交点を抜ける時に閃く。
//   b 十字 = 2本を同時に太めに引き、交点で大きく閃かせ、X の形のまま長めに残す・2本目は濃い紫で色を分ける。
//   c 刃の影 = 細い帯に、剣の形の薄い影を3枚ずつ残す (刃の向きが見える)・少しずらして引く。
export type BladePullFx = 'a' | 'b' | 'c'
export const BLADE_PULL_CROSS = { fwdPx: 122, upPx: 57 } as const
type Pt = readonly [number, number]
/** 3点 (前 px, 上 px) を通る弧を、斬りの跡の形 (中心・半径・角度) にする。 */
export function arcThrough(a: Pt, b: Pt, c: Pt): Pick<ContactSmearArc, 'anchorFwdPx' | 'anchorUpPx' | 'radiusPx' | 'angleFromRad' | 'angleToRad'> {
  const [x1, y1] = a, [x2, y2] = b, [x3, y3] = c
  const d = 2 * (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2))
  const ux = ((x1 * x1 + y1 * y1) * (y2 - y3) + (x2 * x2 + y2 * y2) * (y3 - y1) + (x3 * x3 + y3 * y3) * (y1 - y2)) / d
  const uy = ((x1 * x1 + y1 * y1) * (x3 - x2) + (x2 * x2 + y2 * y2) * (x1 - x3) + (x3 * x3 + y3 * y3) * (x2 - x1)) / d
  const near = (t: number, ref: number): number => t - ref > Math.PI ? near(t - 2 * Math.PI, ref) : t - ref < -Math.PI ? near(t + 2 * Math.PI, ref) : t
  const t1 = Math.atan2(y1 - uy, x1 - ux)
  const t2 = near(Math.atan2(y2 - uy, x2 - ux), t1)
  const t3 = near(Math.atan2(y3 - uy, x3 - ux), t2)
  return { anchorFwdPx: ux, anchorUpPx: uy, radiusPx: Math.hypot(x1 - ux, y1 - uy), angleFromRad: t1, angleToRad: t3 }
}
const C: Pt = [BLADE_PULL_CROSS.fwdPx, BLADE_PULL_CROSS.upPx]
const PULL_FRONT_ARC = arcThrough([188, 100], C, [84, 30])
const PULL_REAR_ARC = arcThrough([188, 8], C, [-80, 26])
const DEEP_VIOLET = '#7b55d6'
const pullPair = (front: Partial<ContactSmearArc>, rear: Partial<ContactSmearArc>): Record<string, ContactSmearArc> => ({
  [PIROUETTE_PULL]: { ...BLADE_SMEAR_BASE, ...PULL_FRONT_ARC, widthPx: 7, trail: 'afterimage', ...front },
  [`${PIROUETTE_PULL}#2`]: { ...BLADE_SMEAR_BASE, ...PULL_REAR_ARC, widthPx: 7, trail: 'afterimage', ...rear },
})
export const BLADE_PULL_FX_SMEAR: Record<BladePullFx, Record<string, ContactSmearArc>> = {
  a: pullPair(
    { trailWidthPx: 11, trailHeadStart: 0.45, afterContact: { fromP: 0.15, revealMs: 70, holdMs: 120, fadeMs: 220 } },
    { trailWidthPx: 11, trailHeadStart: 0.45, afterContact: { fromP: 0.1, revealMs: 70, holdMs: 120, fadeMs: 220 }, delayMs: 80,
      glint: { ...BLADE_PULL_CROSS, atMs: 100, sizePx: 30 } },
  ),
  b: pullPair(
    { trailWidthPx: 17, trailHeadStart: 0.65, afterContact: { fromP: 0.3, revealMs: 50, holdMs: 180, fadeMs: 260 } },
    { trailWidthPx: 17, trailHeadStart: 0.65, afterContact: { fromP: 0.3, revealMs: 50, holdMs: 180, fadeMs: 260 }, glowColor: DEEP_VIOLET,
      glint: { ...BLADE_PULL_CROSS, atMs: 30, sizePx: 48 } },
  ),
  c: pullPair(
    { trailWidthPx: 9, trailHeadStart: 0.4, afterContact: { fromP: 0.15, revealMs: 60, holdMs: 130, fadeMs: 220 }, ghostBlades: { count: 3, lenPx: 78 } },
    { trailWidthPx: 9, trailHeadStart: 0.4, afterContact: { fromP: 0.1, revealMs: 60, holdMs: 130, fadeMs: 220 }, delayMs: 50, ghostBlades: { count: 3, lenPx: 78 },
      glint: { ...BLADE_PULL_CROSS, atMs: 70, sizePx: 24 } },
  ),
}
// 10-27 作者が c 刃の影を選んだ（検収 1027b「右下が良い」）＝未指定は c。?bladePullFx=old で 10-27 の跡、a・b は比べ用に残す。
export function parseBladePullFx(v: string | null): BladePullFx | null {
  if (v === 'old') return null
  return v === 'a' || v === 'b' ? v : 'c'
}

let attackSnap: AttackDef | null = null
let timelineSnap: BossAttackTimeline | undefined
let installed = false

/** 旋回突きをアルレッキーノの4連にする (DEV ?bladePirouette=b4・useBladeDancerTempoY2tz の後に呼ぶ)。 */
export function useBladeDancerPirouetteB4(trail: BladeTrailStyle = 'afterimage'): void {
  install(trail, BLADE_PIROUETTE_B4_TIMELINE, BLADE_PIROUETTE_B4_FRONT_PX, BLADE_PIROUETTE_B4_GAPS_MS, BLADE_PIROUETTE_B4_SMEAR)
}

/** アルレ版 (?bladeArle=1) の5連: ④の低い突きのあと、2本を交差させて体の方へ引き戻す一撃を足す (useBladeDancerPirouetteB4 の代わりに呼ぶ)。 */
//   pullFx = 引き戻しの跡の作り直しの案 (開発版 ?bladePullFx=a|b|c|old・既定 c・null = 10-27 の跡)。
export function useBladeDancerPirouetteArle(trail: BladeTrailStyle = 'afterimage', pullFx: BladePullFx | null = null): void {
  install(trail, BLADE_PIROUETTE_ARLE_TIMELINE, BLADE_PIROUETTE_ARLE_FRONT_PX, BLADE_PIROUETTE_ARLE_GAPS_MS, BLADE_PIROUETTE_ARLE_SMEAR)
  // 案の跡は帯の太さ・残り方まで案ごとに決めてあるので、v6 の太さの決め方を通さずそのまま入れる。
  if (pullFx && trail !== 'petals') Object.assign(SMEAR_ARTS, BLADE_PULL_FX_SMEAR[pullFx])
}

function install(trail: BladeTrailStyle, timeline: BossAttackTimeline, frontPx: readonly number[], gaps: number[], smearTable: Record<string, ContactSmearArc>): void {
  const a = ATTACKS.pirouette!
  if (!installed) {
    attackSnap = { ...a }
    timelineSnap = BOSS_ATTACK_TIMELINES[BOSS_ID]?.pirouette
    installed = true
  }
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), pirouette: timeline }
  const fronts = frontPx.map(px => px + REACH_FIT_MARGIN_PX)
  const approach = Math.min(...fronts)
  const hits = fronts.length
  delete a.unblockable
  Object.assign(a, {
    chargeMs: CHARGE_MS, activeMs: ACTIVE_MS, recoveryMs: RECOVERY_MS,
    parryable: true, defense: 'parry', postureDamage: 20,
    comboHits: hits, comboGapMs: gaps[0], comboGapsMs: [...gaps],
    range: Math.max(...fronts), approachDistancePx: approach,
    strike: { localFromPx: REAR_PX, localToPx: fronts[0]! },
    comboStages: fronts.map(to => ({ strike: { localFromPx: REAR_PX, localToPx: to } })),
    lungeDistance: LUNGE_PX, gapCloseBuffer: STOP_BUFFER_PX,
    preActiveLungeMs: STEP_IN_MS, preActiveLungeDistance: LUNGE_PX,
    preActiveLungeStopGapPx: approach - STOP_BUFFER_PX, preActiveLungeEase: 'smooth',
    comboLunge: { followHits: hits, followSpeed: 420, advanceSpeed: 0, plantFromHit: hits, gapOnly: true, glideRampMs: 120, glideBrakePerSec: 8 },
  } satisfies Partial<AttackDef>)
  const smear = bladeDancerV6Smear(trail, smearTable)
  // 突きの帯は弧の半径から太さを決めると太すぎる (半径 1000px) ので、細い線にする。
  if (trail !== 'petals') smear[P(7)] = { ...smear[P(7)]!, trailWidthPx: THRUST_TRAIL_WIDTH_PX }
  Object.assign(SMEAR_ARTS, smear)
}

/** 配布版の旋回突きへ戻す (テストの後始末用)。 */
export function restoreBladeDancerPirouette(): void {
  if (!installed) return
  const a = ATTACKS.pirouette! as unknown as Record<string, unknown>
  for (const k of Object.keys(a)) delete a[k]
  Object.assign(a, attackSnap)
  const tls = BOSS_ATTACK_TIMELINES[BOSS_ID]
  if (tls) {
    if (timelineSnap) tls.pirouette = timelineSnap
    else delete tls.pirouette
  }
  for (const k of Object.keys({ ...BLADE_PIROUETTE_B4_SMEAR, ...BLADE_PIROUETTE_ARLE_SMEAR })) delete SMEAR_ARTS[k]
  installed = false
}

export function parseBladePirouette(v: string | null): 'b4' | null {
  return v === 'b4' ? 'b4' : null
}
